/*
 * Regras da tela de assinaturas (BBF-147), como o protótipo v2 (`telas-assinaturas.js`) e o app-licencas da fase 2:
 *  - a assinatura é o tenant, gerado pelo backoffice, e não muda;
 *  - a situação de cobrança decide o acesso: somente consulta libera só leitura; aguardando o cartão, suspensa e
 *    cancelada, nada (identity, BBF-144);
 *  - upgrade (mensalidade igual ou maior) vale na hora; downgrade fica para a virada do ciclo, que é o próximo
 *    fechamento da empresa; no trial, tudo vale na hora (BBF-135);
 *  - cancelar vale no fim do ciclo pago, a véspera do próximo fechamento; no trial e aguardando o cartão, na hora
 *    (BBF-136);
 *  - contratar opcional vale na hora; tirar fica para o fim do ciclo pago (BBF-4);
 *  - desconto sobre a mensalidade, com mês final, nunca maior que a mensalidade (BBF-106).
 */

export const MEIO_LABELS = {BOLETO: 'Boleto com Pix', CARTAO: 'Cartão'};
export const getMeioLabel = (meio) => MEIO_LABELS[meio] || meio || '-';

// Situações em que o contrato não tem ciclo pago: cancelar, trocar e tirar opcional valem na hora.
export const SEM_CICLO_PAGO = ['TRIAL', 'AGUARDANDO_CARTAO'];

const ACESSO = {
    TRIAL: {nivel: 'total', texto: 'Acesso a todo o direito, até o fim do trial. No fim, a assinatura entra no ciclo da empresa com a fatura de entrada.'},
    ATIVA: {nivel: 'total', texto: 'Acesso a todo o direito: módulos incluídos e opcionais contratados.'},
    EM_ATRASO: {nivel: 'total', texto: 'Há cobrança vencida, mas o acesso continua inteiro até a régua passar para somente consulta.'},
    SOMENTE_CONSULTA: {nivel: 'leitura', texto: 'Só as funcionalidades de leitura ficam liberadas até a cobrança ser regularizada. O login continua.'},
    AGUARDANDO_CARTAO: {nivel: 'nenhum', texto: 'Sem acesso e fora do faturamento até a empresa cadastrar o cartão no checkout.'},
    SUSPENSA: {nivel: 'nenhum', texto: 'Sem acesso, nem login, até a cobrança ser regularizada.'},
    CANCELADA: {nivel: 'nenhum', texto: 'Sem acesso. A assinatura fica como histórico; reativar volta o acesso.'},
};

export const efeitoNoAcesso = (status) => ACESSO[status] || {nivel: 'nenhum', texto: '-'};

export const vigente = (licenca) => Boolean(licenca) && licenca.status !== 'CANCELADA';

/* ---------- datas do ciclo ---------- */

const dataLocal = (hoje = new Date()) => {
    const partes = new Intl.DateTimeFormat('en-CA', {timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit'})
        .formatToParts(hoje)
        .reduce((acc, parte) => ({...acc, [parte.type]: parte.value}), {});
    return {ano: Number(partes.year), mes: Number(partes.month), dia: Number(partes.day)};
};

const iso = ({ano, mes, dia}) => `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;

/*
 * O próximo fechamento da empresa, no fuso de Brasília, como o app-licencas calcula: o fechamento de hoje já
 * passou. Serve para a tela dizer o efeito antes de confirmar; a data que vale é a que a API devolve.
 */
export const proximoFechamento = (diaFechamento, hoje = new Date()) => {
    const dia = Number(diaFechamento);
    if (!(dia >= 1 && dia <= 28)) return null;
    const agora = dataLocal(hoje);
    if (agora.dia < dia) return iso({ano: agora.ano, mes: agora.mes, dia});
    const mes = agora.mes === 12 ? 1 : agora.mes + 1;
    return iso({ano: agora.mes === 12 ? agora.ano + 1 : agora.ano, mes, dia});
};

// O fim do ciclo pago: a véspera do próximo fechamento.
export const fimDoCicloPago = (diaFechamento, hoje = new Date()) => {
    const fechamento = proximoFechamento(diaFechamento, hoje);
    if (!fechamento) return null;
    const [ano, mes, dia] = fechamento.split('-').map(Number);
    const vespera = new Date(Date.UTC(ano, mes - 1, dia - 1));
    return iso({ano: vespera.getUTCFullYear(), mes: vespera.getUTCMonth() + 1, dia: vespera.getUTCDate()});
};

export const dataBR = (valor) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(valor || ''));
    return match ? `${match[3]}/${match[2]}/${match[1]}` : '-';
};

// `2026-12` → `12/2026`.
export const mesBR = (valor) => {
    const match = /^(\d{4})-(\d{2})$/.exec(String(valor || ''));
    return match ? `${match[2]}/${match[1]}` : '-';
};

/* ---------- o efeito de cada ação, antes de confirmar ---------- */

const reais = (valor) => new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(Number(valor) || 0);

// Troca de plano ou de versão: a mesma regra do app-licencas (preço da versão alvo contra o da atual).
export const efeitoDaTroca = (licenca, alvo, diaFechamento, hoje = new Date()) => {
    const atual = Number(licenca?.versao?.preco_base_mensal) || 0;
    const novo = Number(alvo?.preco_base_mensal) || 0;
    const upgrade = novo >= atual;
    if (upgrade) {
        return {tipo: 'UPGRADE', agora: true, texto: `Upgrade: vale agora. O direito muda na hora, e a nova mensalidade (${reais(novo)}) entra na fatura seguinte, sem pró-rata.`};
    }
    if (SEM_CICLO_PAGO.includes(licenca?.status)) {
        return {tipo: 'DOWNGRADE', agora: true, texto: `Downgrade em trial: vale agora, porque a assinatura ainda não tem ciclo pago. A mensalidade passa a ${reais(novo)}.`};
    }
    const data = proximoFechamento(diaFechamento, hoje);
    return {
        tipo: 'DOWNGRADE',
        agora: false,
        data,
        texto: `Downgrade: fica agendado para ${data ? dataBR(data) : 'o próximo fechamento'}, a virada do ciclo. Até lá, nada muda, e dá para desfazer antes. A mensalidade passa a ${reais(novo)}.`,
    };
};

export const efeitoDoCancelamento = (licenca, diaFechamento, hoje = new Date()) => {
    if (SEM_CICLO_PAGO.includes(licenca?.status)) {
        return {agora: true, texto: 'Cancela agora, sem cobrança: a assinatura ainda não tem ciclo pago. O acesso termina na hora.'};
    }
    const data = fimDoCicloPago(diaFechamento, hoje);
    return {
        agora: false,
        data,
        texto: `O acesso continua até ${data ? dataBR(data) : 'o fim do ciclo pago'}, o fim do ciclo pago. O excedente medido vai numa fatura de encerramento. Dá para desistir até lá.`,
    };
};

export const efeitoDeTirarOpcional = (licenca, diaFechamento, hoje = new Date()) => {
    if (SEM_CICLO_PAGO.includes(licenca?.status)) return 'Sai na hora: a assinatura ainda não tem ciclo pago.';
    const data = fimDoCicloPago(diaFechamento, hoje);
    return `Continua até ${data ? dataBR(data) : 'o fim do ciclo pago'}, o fim do ciclo pago, e sai na virada.`;
};

/* ---------- desconto ---------- */

export const mesAtual = (hoje = new Date()) => {
    const agora = dataLocal(hoje);
    return `${agora.ano}-${String(agora.mes).padStart(2, '0')}`;
};

// As mesmas regras do app-licencas, para o erro aparecer antes de ir à API.
export const validarDesconto = ({tipo, valor, ate, motivo}, mensalidade, hoje = new Date()) => {
    const numero = Number(String(valor).replace(',', '.'));
    if (!['PERCENTUAL', 'VALOR'].includes(tipo)) return 'Escolha percentual ou em reais.';
    if (!(numero > 0)) return 'Informe o valor do desconto, maior que zero.';
    if (tipo === 'PERCENTUAL' && numero > 100) return 'O percentual vai até 100.';
    if (tipo === 'VALOR' && numero > Number(mensalidade)) return `O desconto em reais vai até a mensalidade da versão do plano (${reais(mensalidade)}).`;
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(ate || '')) return 'Informe o mês final.';
    if (ate < mesAtual(hoje)) return 'O mês final já passou.';
    if (!motivo?.trim()) return 'Informe o motivo. Ele aparece na fatura.';
    if (motivo.trim().length > 200) return 'O motivo tem no máximo 200 caracteres.';
    return '';
};

export const descricaoDoDesconto = (desconto) => {
    if (!desconto) return '';
    // O valor vem como número ou como texto ("10.00", do histórico): 10% e não 10,00%.
    const valor = desconto.tipo === 'PERCENTUAL'
        ? `${Number(desconto.valor).toLocaleString('pt-BR', {maximumFractionDigits: 2})}%`
        : reais(desconto.valor);
    return `${valor} sobre a mensalidade, até ${mesBR(desconto.ate)}. ${desconto.motivo || ''}`.trim();
};

// O desconto deixa de valer depois do mês final.
export const descontoVale = (desconto, hoje = new Date()) => Boolean(desconto?.ate) && desconto.ate >= mesAtual(hoje);

/* ---------- histórico ---------- */

const TEXTOS_DO_CONTRATO = {
    CANCELAMENTO_AGENDADO: (d) => `Cancelamento agendado para ${dataBR(d.agendado_para)}`,
    CANCELAMENTO_DESFEITO: () => 'Cancelamento desfeito',
    DESCONTO_DADO: (d) => `Desconto dado: ${descricaoDoDesconto(d)}`,
    DESCONTO_ALTERADO: (d) => `Desconto alterado: ${descricaoDoDesconto(d)}`,
    DESCONTO_REMOVIDO: () => 'Desconto removido',
    TROCA_DE_PLANO: (d) => `${d.tipo === 'DOWNGRADE' ? 'Downgrade' : 'Upgrade'} para ${d.para?.nome_plano || 'outro plano'} v${d.para?.numero ?? '?'}, na hora`,
    TROCA_AGENDADA: (d) => `Downgrade agendado para ${d.para?.nome_plano || 'outro plano'} v${d.para?.numero ?? '?'}, em ${dataBR(d.vale_a_partir_de)}`,
    TROCA_DESFEITA: () => 'Troca de plano desfeita',
    OPCIONAL_CONTRATADO: (d) => `Opcional contratado: ${d.nome || 'módulo'}`,
    OPCIONAL_SAIDA_AGENDADA: (d) => `Saída de opcional agendada${d.nome ? ` (${d.nome})` : ''} para ${dataBR(d.sai_apos)}`,
    OPCIONAL_SAIDA_DESFEITA: (d) => `Saída de opcional desfeita${d.nome ? `: ${d.nome}` : ''}`,
    OPCIONAL_RETIRADO: (d) => `Opcional retirado${d.nome ? `: ${d.nome}` : ''}`,
};

const STATUS_LABELS = {
    AGUARDANDO_CARTAO: 'aguardando cartão', TRIAL: 'trial', ATIVA: 'ativa', EM_ATRASO: 'em atraso',
    SOMENTE_CONSULTA: 'somente consulta', SUSPENSA: 'suspensa', CANCELADA: 'cancelada',
};

const textoDaSituacao = ({de, para}) => {
    const nome = (status) => STATUS_LABELS[status] || status;
    if (!de) return `Criada ${STATUS_LABELS[para] ? `como ${nome(para)}` : ''}`.trim();
    // A migração da 0063 gravou a situação de cada licença como "de X para X".
    if (de === para) return `Situação registrada: ${nome(para)}`;
    return `Situação: de ${nome(de)} para ${nome(para)}`;
};

/*
 * Uma linha do tempo só, com a situação e o contrato, do mais recente para o mais antigo. O nome do opcional vem
 * de `nomesDeModulo` quando o evento não o guardou.
 */
export const linhaDoTempo = (historico, nomesDeModulo = {}) => {
    const situacao = (historico?.items || []).map((item) => ({
        em: item.em,
        texto: textoDaSituacao(item),
        motivo: item.motivo,
        quem: item.quem,
    }));
    const contrato = (historico?.contrato || []).map((item) => {
        const detalhe = {...(item.detalhe || {})};
        if (!detalhe.nome && detalhe.id_modulo && nomesDeModulo[detalhe.id_modulo]) detalhe.nome = nomesDeModulo[detalhe.id_modulo];
        const texto = TEXTOS_DO_CONTRATO[item.evento];
        return {em: item.em, texto: texto ? texto(detalhe) : item.evento, motivo: item.motivo, quem: item.quem};
    });
    return [...situacao, ...contrato].sort((a, b) => String(b.em || '').localeCompare(String(a.em || '')));
};

/* ---------- troca de plano ---------- */

// Para onde a assinatura pode ir: a versão à venda de cada plano do mesmo sistema, menos a versão em que ela está.
export const destinosDaTroca = (licenca, planos = []) => planos
    .filter((plano) => plano.versao_a_venda && plano.versao_a_venda.id !== licenca?.id_plano_versao)
    .map((plano) => ({
        id_plano: plano.id,
        nome_plano: plano.nome_plano,
        ...plano.versao_a_venda,
        mesmoPlano: plano.id === licenca?.id_plano,
    }))
    .sort((a, b) => Number(b.mesmoPlano) - Number(a.mesmoPlano) || (Number(a.preco_base_mensal) - Number(b.preco_base_mensal)));

// O que muda nos opcionais e no meio quando a troca vale (o banco faz isso, a tela só avisa).
export const avisosDaTroca = (licenca, alvo) => {
    const avisos = [];
    if (alvo?.meios_aceitos?.length && !alvo.meios_aceitos.includes(licenca?.meio_pagamento)) {
        avisos.push(`A versão nova não aceita ${getMeioLabel(licenca?.meio_pagamento).toLowerCase()}: o meio passa a ${getMeioLabel(alvo.meios_aceitos[0]).toLowerCase()}.`);
    }
    if ((licenca?.opcionais || []).length) avisos.push('Os opcionais que a versão nova não oferece saem quando a troca valer.');
    if (licenca?.desconto) avisos.push('O desconto continua até o mês final.');
    return avisos;
};

/* ---------- criação ---------- */

const codigoDaEmpresa = (empresa) => (empresa?.nome_fantasia || empresa?.razao_social || 'BF')
    .normalize('NFD')
    .replace(/[^A-Za-z]/g, '')
    .slice(0, 3)
    .toUpperCase() || 'BF';

// A chave de ativação que o POST ainda exige. O tenant, não: o backoffice gera.
export const novaChaveDeAtivacao = (empresa, agora = Date.now(), aleatorio = Math.random) => {
    const parte = aleatorio().toString(36).substring(2, 8).toUpperCase();
    return `BF-${codigoDaEmpresa(empresa)}-${parte}-${agora.toString(36).toUpperCase()}`;
};

/*
 * Em que situação a assinatura nasce, como no protótipo v2: com cartão, aguardando o cartão (o trial começa quando a
 * empresa cadastra o cartão no checkout); com boleto, ativa.
 */
export const situacaoInicial = (meio) => (meio === 'CARTAO' ? 'AGUARDANDO_CARTAO' : 'ATIVA');
